import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    AlertCircle,
    CheckCircle2,
    Download,
    FileText,
    IndianRupee,
    Plus,
    RefreshCcw,
    Search,
    ShieldCheck,
    UserCheck
} from "lucide-react";
import Sidebar from "../../components/Sidebar/sidebar";
import Header from "../../components/Header";
import Pagination from "../../components/Pagination/Pagination";
import api from '../../utils/api'
import "./Claims.css";
import { API_BASE_URL } from '../../utils/apiBase';
import { getUserPrivileges, getUserRole } from "../../utils/auth";
import { MANAGE_POLICY_PRIVILEGES } from "../../utils/managePolicyPrivileges";

const createEmptySurveyorDetails = () => ({
    appointmentDate: "",
    surveyorName: "",
    phoneNumber: "",
    email: "",
    address: "",
    irdaLicenseNumber: "",
    requirements: ""
});

const Claims = () => {
    const userPrivileges = getUserPrivileges();
    const isAdmin = getUserRole() === "admin";
    const hasPrivilege = (key) => isAdmin || userPrivileges.includes(key);
    const canAddClaim = hasPrivilege(MANAGE_POLICY_PRIVILEGES.claims.add);
    const canExportClaims = hasPrivilege(MANAGE_POLICY_PRIVILEGES.claims.export);

    const [collapsed, setCollapsed] = useState(false);

    const [policySearchInput, setPolicySearchInput] = useState("");
    const [policySuggestions, setPolicySuggestions] = useState([]);
    const [policyFocused, setPolicyFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const [policyData, setPolicyData] = useState(null);
    const [claimDoc, setClaimDoc] = useState(null);
    const [showClaimPopup, setShowClaimPopup] = useState(false);
    const [claims, setClaims] = useState([]);
    const [claimsLoading, setClaimsLoading] = useState(false);
    const isPolicySelected = !!policyData;
    const [showSurveyorPopup, setShowSurveyorPopup] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editClaimData, setEditClaimData] = useState(null);
    const [showSettlementPopup, setShowSettlementPopup] = useState(false);
    const [settlementClaim, setSettlementClaim] = useState(null);
    const [statusSurveyorClaim, setStatusSurveyorClaim] = useState(null);
    const [statusSurveyorData, setStatusSurveyorData] = useState(createEmptySurveyorDetails());

    const [settlementData, setSettlementData] = useState({
        finalStatus: "",
        settlementDate: new Date().toISOString().slice(0, 10),
        amountPaid: "",
        rejectionReason: ""
    });





    const [formData, setFormData] = useState({
        policyId: "",
        policyNumber: "",
        clientType: "",
        // claimStatus: "Initiated",
        dateOfLoss: "",
        dateOfIntimation: "",
        typeOfLoss: "",
        estimatedLoss: "",
        causeOfLoss: ""
    });



    const [surveyorData, setSurveyorData] = useState({
        appointmentDate: "",
        surveyorName: "",
        phoneNumber: "",
        email: "",
        address: "",
        irdaLicenseNumber: "", // ✅ rename
        requirements: ""
    });


    const fetchClaims = useCallback(async () => {
        setClaimsLoading(true);
        try {
            const res = await api.get(`${API_BASE_URL}/api/claims`, {
                params: {
                    q: searchText || undefined,
                    status: statusFilter || undefined,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined
                }
            });

            setClaims(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setClaimsLoading(false);
        }
    }, [searchText, statusFilter, fromDate, toDate]);

    useEffect(() => {
        fetchClaims();
    }, [fetchClaims]);

    const pageLast = currentPage * itemsPerPage;
    const pageFirst = pageLast - itemsPerPage;
    const currentClaims = claims.slice(pageFirst, pageLast);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, statusFilter, fromDate, toDate, itemsPerPage]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(claims.length / itemsPerPage));
        setCurrentPage((page) => Math.min(page, totalPages));
    }, [claims.length, itemsPerPage]);

    const formatCurrency = (value) =>
        `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

    const formatDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("en-IN");
    };

    const getStatusClass = (status = "") => {
        const normalized = status.toLowerCase().replace(/\s+/g, "-");
        if (normalized.includes("settled") || normalized.includes("approved") || normalized.includes("paid")) {
            return "is-success";
        }
        if (normalized.includes("surveyor")) {
            return "is-info";
        }
        if (normalized.includes("pending") || normalized.includes("documents")) {
            return "is-warning";
        }
        if (normalized.includes("rejected")) {
            return "is-danger";
        }
        return "is-neutral";
    };

    const summaryCards = useMemo(() => {
        const totalAmount = claims.reduce((sum, claim) => sum + Number(claim.estimatedLoss || 0), 0);
        const countByStatus = (matcher) =>
            claims.filter((claim) => matcher((claim.claimStatus || "").toLowerCase())).length;

        return [
            {
                label: "Total Claims",
                value: claims.length,
                trend: "All active claim records",
                icon: FileText,
                tone: "blue"
            },
            {
                label: "Pending Claims",
                value: countByStatus((status) => status.includes("pending") || status.includes("intimated")),
                trend: "Needs follow-up",
                icon: AlertCircle,
                tone: "amber"
            },
            {
                label: "Settled Claims",
                value: countByStatus((status) => status.includes("settled") || status.includes("paid")),
                trend: "Closed successfully",
                icon: CheckCircle2,
                tone: "green"
            },
            {
                label: "Surveyor Assigned",
                value: countByStatus((status) => status.includes("surveyor")),
                trend: "In assessment",
                icon: UserCheck,
                tone: "violet"
            },
            {
                label: "Total Claim Amount",
                value: formatCurrency(totalAmount),
                trend: "Estimated exposure",
                icon: IndianRupee,
                tone: "slate"
            }
        ];
    }, [claims]);



    // LIVE SEARCH POLICY
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!policyFocused || policySearchInput.trim().length < 1) {
                setPolicySuggestions([]);
                return;
            }

            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/claims/search-policy?query=${policySearchInput}`
                );
                setPolicySuggestions(res.data);
            } catch (err) {
                console.error(err);
            }
        };

        const timer = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(timer);
    }, [policySearchInput, policyFocused]);


    // SELECT POLICY
    const handlePolicySelect = async (policy) => {
        setPolicySearchInput(policy.policyNumber);
        setPolicySuggestions([]);

        try {
            const res = await api.get(
                `${API_BASE_URL}/api/claims/policy`,
                {
                    params: { number: policy.policyNumber }
                }
            );
            setPolicyData(res.data);

            setFormData(prev => ({
                ...prev,
                policyId: res.data._id,
                policyNumber: res.data.policyNumber,
                clientType: res.data.clientType,
                dateOfIntimation: new Date().toISOString().slice(0, 10)
            }));
        } catch (err) {
            alert("Error loading policy details");
        }
    };


    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const exportToExcel = () => {
        if (!claims.length) {
            alert("No data to export");
            return;
        }

        const formattedData = claims.map(c => ({
            "Claim Number": c.claimNumber,
            "Policy Number": c.policyNumber,
            "Type of Loss": c.typeOfLoss,
            "Date of Loss": c.dateOfLoss
                ? new Date(c.dateOfLoss).toLocaleDateString("en-IN")
                : "",
            "Estimated Loss": c.estimatedLoss,
            "Status": c.claimStatus
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Claims");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const file = new Blob(
            [excelBuffer],
            { type: "application/octet-stream" }
        );

        saveAs(file, `Claims_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleUpdateClaim = async () => {
        try {
            await api.put(
                `${API_BASE_URL}/api/claims/update/${selectedClaim._id}`,
                {
                    dateOfLoss: editClaimData.dateOfLoss,
                    typeOfLoss: editClaimData.typeOfLoss,
                    estimatedLoss: editClaimData.estimatedLoss,
                    causeOfLoss: editClaimData.causeOfLoss,
                    claimStatus: editClaimData.claimStatus,
                    surveyorAppointment: editClaimData.surveyorAppointment
                }
            );

            alert("Claim updated successfully");

            setIsEditMode(false);
            setSelectedClaim(null);
            fetchClaims(); // refresh table

        } catch (err) {
            console.error(err);
            alert("Failed to update claim");
        }
    };

    const updateClaimStatus = async (id, status, extraPayload = {}) => {
        await api.put(
            `${API_BASE_URL}/api/claims/update/${id}`,
            { claimStatus: status, ...extraPayload }
        );
        fetchClaims();
    };

    const handleClaimNumberClick = async (claim) => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/claims/details/${claim._id}`);
            setSelectedClaim(res.data);
            setEditClaimData(res.data);
            setIsEditMode(false);
        } catch (err) {
            console.error("Error loading claim details:", err);
            alert("Failed to load claim details");
        }
    };

    const handleClaimAction = (claim, newStatus) => {
        if (newStatus === "Settled") {
            setSettlementClaim(claim);
            setShowSettlementPopup(true);
        } else if (newStatus === "Surveyor Assigned") {
            setStatusSurveyorClaim(claim);
            setStatusSurveyorData({
                ...createEmptySurveyorDetails(),
                ...(claim.surveyorAppointment || {}),
                appointmentDate: claim.surveyorAppointment?.appointmentDate
                    ? claim.surveyorAppointment.appointmentDate.slice(0, 10)
                    : new Date().toISOString().slice(0, 10)
            });
        } else {
            updateClaimStatus(claim._id, newStatus);
        }
    };

    const handleSurveyorStatusSave = async () => {
        if (
            !statusSurveyorData.appointmentDate ||
            !statusSurveyorData.surveyorName ||
            !statusSurveyorData.phoneNumber ||
            !statusSurveyorData.email ||
            !statusSurveyorData.irdaLicenseNumber ||
            !statusSurveyorData.requirements
        ) {
            alert("Please fill all required surveyor details");
            return;
        }

        try {
            await updateClaimStatus(statusSurveyorClaim._id, "Surveyor Assigned", {
                surveyorAppointment: statusSurveyorData
            });
            setStatusSurveyorClaim(null);
            setStatusSurveyorData(createEmptySurveyorDetails());
        } catch (err) {
            console.error("Surveyor status update error:", err);
            alert(err?.response?.data?.error || "Failed to assign surveyor");
        }
    };

    const handleSettlementSave = async () => {
        if (!settlementData.finalStatus) {
            alert("Select final status");
            return;
        }

        await api.put(
            `${API_BASE_URL}/api/claims/update/${settlementClaim._id}`,
            {
                claimStatus: "Settled",
                settlement: {
                    finalStatus: settlementData.finalStatus,
                    settlementDate: settlementData.settlementDate,
                    amountPaid:
                        settlementData.finalStatus === "Approved"
                            ? settlementData.amountPaid
                            : undefined,
                    rejectionReason:
                        settlementData.finalStatus === "Rejected"
                            ? settlementData.rejectionReason
                            : undefined
                }
            }
        );

        setShowSettlementPopup(false);
        setSettlementClaim(null);
        fetchClaims();
    };




    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = new FormData();

            Object.entries(formData).forEach(([key, val]) => {
                payload.append(key, val);
            });

            // Surveyor popup data
            const hasSurveyor =
                surveyorData.surveyorName &&
                surveyorData.irdaLicenseNumber;

            if (hasSurveyor) {
                payload.append(
                    "surveyorDetails",
                    JSON.stringify(surveyorData)
                );
            }


            // Claim document
            if (claimDoc) {
                payload.append("claimDoc", claimDoc);
            }

            await api.post(
                `${API_BASE_URL}/api/claims/add`,
                payload,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            alert("Claim Submitted Successfully!");

            setShowClaimPopup(false);

            // Reset form
            setFormData({
                policyId: "",
                policyNumber: "",
                clientType: "",
                dateOfLoss: "",
                dateOfIntimation: "",
                typeOfLoss: "",
                estimatedLoss: "",
                causeOfLoss: ""
            });

            setSurveyorData({
                appointmentDate: "",
                surveyorName: "",
                phoneNumber: "",
                email: "",
                address: "",
                irdaLicenseNumber: "",
                requirements: ""
            });

            setClaimDoc(null);
            setPolicyData(null);
            setPolicySearchInput("");

        } catch (err) {
            console.error("Submit claim error:", err);
            alert(
                err?.response?.data?.error ||
                "Error submitting claim"
            );
        }
    };



    return (
        <div className="claims-container">
            <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

            <div className={`claims-content ${collapsed ? "collapsed" : ""}`}>
                <div className="claims-sticky-header">
                    <Header />
                </div>

                <div className="claims-scroll-area">
                    <section className="claims-page-shell">
                        <div className="claims-page-header">
                            <div>
                                <span className="claims-eyebrow">Manage Policy</span>
                                <h1>Claims Management</h1>
                                <p>Track and manage policy claims, settlements, surveyor actions, and claim exposure in one workspace.</p>
                            </div>
                            {canAddClaim && (
                                <button
                                    className="add-claim-btn"
                                    onClick={() => setShowClaimPopup(true)}
                                >
                                    <Plus size={18} />
                                    Add Claim
                                </button>
                            )}
                        </div>

                        <div className="claims-summary-grid">
                            {summaryCards.map(({ label, value, trend, icon: Icon, tone }) => (
                                <article className={`claims-summary-card tone-${tone}`} key={label}>
                                    <div className="summary-icon">
                                        <Icon size={20} />
                                    </div>
                                    <div>
                                        <span>{label}</span>
                                        <strong>{value}</strong>
                                        <small>{trend}</small>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div className="claims-toolbar-card">
                            <div className="toolbar-title">
                                <div>
                                    <h2>Claims Register</h2>
                                    <p>Search by claim number, policy number, or client name.</p>
                                </div>
                                <div className="toolbar-actions">
                                    <button
                                        className="claims-ghost-btn"
                                        onClick={() => {
                                            setSearchText("");
                                            setStatusFilter("");
                                            setFromDate("");
                                            setToDate("");
                                        }}
                                    >
                                        <RefreshCcw size={16} />
                                        Reset
                                    </button>
                                    {canExportClaims && (
                                        <button
                                            className="export-btn"
                                            onClick={exportToExcel}
                                            disabled={claimsLoading}
                                        >
                                            <Download size={16} />
                                            {claimsLoading ? "Preparing..." : "Export"}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="claims-filters">
                                <label className="claims-search-field">
                                    <Search size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search claim, policy, or client"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </label>

                                <label>
                                    <span>Status</span>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="Intimated">Intimated</option>
                                        <option value="Surveyor Assigned">Surveyor Assigned</option>
                                        <option value="Documents Pending">Documents Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Settled">Settled</option>
                                    </select>
                                </label>

                                <label>
                                    <span>From</span>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </label>

                                <label>
                                    <span>To</span>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </label>
                            </div>
                        </div>

                        {showClaimPopup && (
                            <div className="modal-overlay" onClick={() => setShowClaimPopup(false)}>
                                <div
                                    className="modal-box"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* HEADER */}
                                    <div className="modal-header">
                                        <h2>Add Claim</h2>
                                        <button
                                            className="close-btn"
                                            onClick={() => setShowClaimPopup(false)}
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    {/* 🔍 POLICY SEARCH */}
                                    <div className="policy-search-box" style={{ position: "relative" }}>
                                        <label>Search Policy</label>
                                        <input
                                            type="text"
                                            placeholder="Type Policy Number..."
                                            value={policySearchInput}
                                            onChange={(e) => {
                                                setPolicySearchInput(e.target.value);
                                                setSelectedIndex(-1);
                                            }}
                                            onFocus={() => setPolicyFocused(true)}
                                            onBlur={() =>
                                                setTimeout(() => {
                                                    setPolicySuggestions([]);
                                                    setPolicyFocused(false);
                                                }, 200)
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === "ArrowDown") {
                                                    setSelectedIndex(prev =>
                                                        Math.min(prev + 1, policySuggestions.length - 1)
                                                    );
                                                } else if (e.key === "ArrowUp") {
                                                    setSelectedIndex(prev =>
                                                        Math.max(prev - 1, 0)
                                                    );
                                                } else if (e.key === "Enter" && selectedIndex !== -1) {
                                                    handlePolicySelect(policySuggestions[selectedIndex]);
                                                }
                                            }}
                                        />

                                        {policyFocused && policySuggestions.length > 0 && (
                                            <ul className="policy-suggestions">
                                                {policySuggestions.map((item, idx) => (
                                                    <li
                                                        key={idx}
                                                        className={selectedIndex === idx ? "highlight" : ""}
                                                        onMouseDown={() => handlePolicySelect(item)}
                                                    >
                                                        <b>Policy Number: </b><small>{item.policyNumber}</small><br />
                                                        <b>Client Name: </b><small>{item.clientName}</small><br />
                                                        <b>Insurer Name: </b><small>{item.insurerName}</small>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {/* 📄 POLICY DETAILS + CLAIM FORM */}
                                    <div className="policy-details-box">
                                        {policyData ? (
                                            <>
                                                <p><b>Client:</b> {policyData.clientName}</p>
                                                <p><b>Insurer:</b> {policyData.insurerName}</p>
                                                <p><b>Policy Name:</b> {policyData.policyName}</p>
                                            </>
                                        ) : (
                                            <p style={{ color: "#888" }}>
                                                Please search and select a policy
                                            </p>
                                        )}
                                    </div>

                                    <form className="claim-form" onSubmit={handleSubmit}>
                                        <label>Date of Loss</label>
                                        <input
                                            type="date"
                                            name="dateOfLoss"
                                            value={formData.dateOfLoss}
                                            min={policyData?.policyStartDate?.slice(0, 10)}
                                            max={policyData?.policyEndDate?.slice(0, 10)}
                                            onChange={handleChange}
                                            required
                                        />



                                        <label>Date of Intimation</label>
                                        <input
                                            type="date"
                                            name="dateOfIntimation"
                                            value={formData.dateOfIntimation}
                                            min={policyData?.policyStartDate?.slice(0, 10)}
                                            max={policyData?.policyEndDate?.slice(0, 10)}
                                            onChange={handleChange}
                                            required
                                        />

                                        <label>Type of Loss</label>
                                        <select
                                            name="typeOfLoss"
                                            value={formData.typeOfLoss}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Type</option>
                                            <option value="Property">Property</option>
                                            <option value="Marine">Marine</option>
                                            <option value="Liability">Liability</option>
                                            <option value="Other">Other</option>
                                        </select>

                                        <label>Estimated Loss (₹)</label>
                                        <input
                                            type="text"
                                            name="estimatedLoss"
                                            value={formData.estimatedLoss}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[^\d]/g, "");
                                                setFormData(prev => ({ ...prev, estimatedLoss: val }));
                                            }}
                                            placeholder="₹ Enter amount"
                                            required
                                        />

                                        <label>Cause of Loss</label>
                                        <textarea
                                            name="causeOfLoss"
                                            value={formData.causeOfLoss}
                                            maxLength={100}
                                            onChange={handleChange}
                                            placeholder="Max 100 characters"
                                            required
                                        />

                                        <button
                                            type="button"
                                            className="secondary-btn"
                                            onClick={() => setShowSurveyorPopup(true)}
                                        >
                                            + Surveyor Appointment
                                        </button>
                                        {showSurveyorPopup && (
                                            <div className="modal-overlay">
                                                <div className="modal-box">
                                                    <h3>Surveyor Appointment</h3>

                                                    <label>Date of Appointment</label>
                                                    <input
                                                        type="date"
                                                        value={surveyorData.appointmentDate}
                                                        onChange={(e) =>
                                                            setSurveyorData({ ...surveyorData, appointmentDate: e.target.value })
                                                        }
                                                        required
                                                    />

                                                    <label>Surveyor Name</label>
                                                    <input
                                                        type="text"
                                                        value={surveyorData.surveyorName}
                                                        onChange={(e) =>
                                                            setSurveyorData({ ...surveyorData, surveyorName: e.target.value })
                                                        }
                                                        required
                                                    />

                                                    <label>Phone Number</label>
                                                    <input
                                                        type="text"
                                                        maxLength={10}
                                                        value={surveyorData.phoneNumber}
                                                        onChange={(e) =>
                                                            setSurveyorData({
                                                                ...surveyorData,
                                                                phoneNumber: e.target.value.replace(/[^\d]/g, "")
                                                            })
                                                        }
                                                        required
                                                    />

                                                    <label>Email</label>
                                                    <input
                                                        type="email"
                                                        value={surveyorData.email}
                                                        onChange={(e) =>
                                                            setSurveyorData({ ...surveyorData, email: e.target.value })
                                                        }
                                                        required
                                                    />

                                                    <label>Address</label>
                                                    <textarea
                                                        value={surveyorData.address}
                                                        onChange={(e) =>
                                                            setSurveyorData({ ...surveyorData, address: e.target.value })
                                                        }
                                                    />

                                                    <label>IRDAI License Number</label>
                                                    <input
                                                        type="text"
                                                        value={surveyorData.irdaLicenseNumber}
                                                        onChange={(e) =>
                                                            setSurveyorData({
                                                                ...surveyorData,
                                                                irdaLicenseNumber: e.target.value
                                                            })
                                                        }
                                                        required
                                                    />


                                                    <label>Surveyor Requirements</label>
                                                    <textarea
                                                        value={surveyorData.requirements}
                                                        onChange={(e) =>
                                                            setSurveyorData({ ...surveyorData, requirements: e.target.value })
                                                        }
                                                        required
                                                    />

                                                    <div className="modal-actions">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowSurveyorPopup(false)}
                                                            className="submit-btn"
                                                        >
                                                            Save Surveyor
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}


                                        <small>{formData.causeOfLoss.length}/100</small>

                                        <label>Upload Claim Document</label>
                                        <input
                                            type="file"
                                            disabled={!isPolicySelected}
                                            onChange={(e) => setClaimDoc(e.target.files[0])}
                                            required
                                        />

                                        <div className="modal-actions">
                                            <button type="submit" className="submit-btn">
                                                Submit Claim
                                            </button>

                                            <button
                                                type="button"
                                                className="cancel-btn"
                                                onClick={() => setShowClaimPopup(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}


                        <div className="claims-list">
                            <div className="claims-table-header">
                                <div>
                                    <h3>Claims List</h3>
                                    <p>{claims.length} records currently visible</p>
                                </div>
                                <ShieldCheck size={22} />
                            </div>

                            <div className="claims-table-wrap">
                                <table className="claims-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-col sticky-col-first">Claim No</th>
                                            <th className="sticky-col sticky-col-second">Policy No</th>
                                            <th>Type of Loss</th>
                                            <th>Date of Loss</th>
                                            <th>Estimated Loss</th>
                                            <th>Status</th>
                                            <th>Update</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {claimsLoading ? (
                                            Array.from({ length: 5 }).map((_, index) => (
                                                <tr className="skeleton-row" key={`claim-skeleton-${index}`}>
                                                    <td className="sticky-col sticky-col-first"><span /></td>
                                                    <td className="sticky-col sticky-col-second"><span /></td>
                                                    <td><span /></td>
                                                    <td><span /></td>
                                                    <td><span /></td>
                                                    <td><span /></td>
                                                    <td><span /></td>
                                                </tr>
                                            ))
                                        ) : claims.length ? (
                                            currentClaims.map((c) => (
                                                <tr key={c._id}>
                                                    <td className="sticky-col sticky-col-first" data-label="Claim No">
                                                        <div className="claims-primary-cell">
                                                            <button
                                                                className="claim-link"
                                                                onClick={() => handleClaimNumberClick(c)}
                                                            >
                                                                {c.claimNumber}
                                                            </button>
                                                            <small>View details</small>
                                                        </div>
                                                    </td>

                                                    <td className="sticky-col sticky-col-second" data-label="Policy No">
                                                        <div className="claims-policy-cell">
                                                            <strong>{c.policyNumber}</strong>
                                                            {c.clientName && <small>{c.clientName}</small>}
                                                        </div>
                                                    </td>
                                                    <td data-label="Type of Loss">
                                                        <span className="loss-type-pill">{c.typeOfLoss || "Other"}</span>
                                                    </td>
                                                    <td data-label="Date of Loss">
                                                        {c.dateOfLoss
                                                            ? new Date(c.dateOfLoss).toLocaleDateString("en-IN")
                                                            : "-"}
                                                    </td>

                                                    <td data-label="Estimated Loss" className="claim-amount">
                                                        {formatCurrency(c.estimatedLoss)}
                                                    </td>
                                                    <td data-label="Status">
                                                        <span className={`status-badge ${getStatusClass(c.claimStatus)}`}>
                                                            {c.claimStatus || "Pending"}
                                                        </span>
                                                    </td>
                                                    <td data-label="Update">
                                                        {canAddClaim ? (
                                                            <select
                                                                className="claim-status-select"
                                                                value={c.claimStatus}
                                                                onChange={(e) =>
                                                                    handleClaimAction(c, e.target.value)
                                                                }
                                                            >
                                                                <option value="Intimated">Intimated</option>
                                                                <option value="Surveyor Assigned">Surveyor Assigned</option>
                                                                <option value="Settled">Settled</option>
                                                            </select>
                                                        ) : (
                                                            <span className="status-badge is-neutral">Restricted</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="claims-empty-state">
                                                        <FileText size={34} />
                                                        <strong>No claims found</strong>
                                                        <span>Try adjusting filters or add a new claim to begin.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {claims.length > 0 && (
                            <div className="claims-pagination-row">
                                <div className="claims-pagination-meta">
                                    <span>
                                        Showing {pageFirst + 1}-{Math.min(pageLast, claims.length)} of {claims.length}
                                    </span>
                                    <small>Filtered claim records</small>
                                </div>

                                <div className="claims-pagination-controls">
                                    <Pagination
                                        totalItems={claims.length}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>

                                <div className="claims-items-per-page">
                                    <label>Items per page:</label>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        {selectedClaim && (
                            <div className="modal-overlay" onClick={() => setSelectedClaim(null)}>
                                <div
                                    className="modal-box"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="modal-header">
                                        <h3>Claim Details</h3>
                                        <button onClick={() => setSelectedClaim(null)}>✕</button>
                                    </div>

                                    <div className="claim-details">
                                        <h4>Full Claim Information</h4>
                                        <div className="claim-detail-grid">
                                            <div><span>Claim Number</span><strong>{selectedClaim.claimNumber || "-"}</strong></div>
                                            <div><span>Status</span><strong>{selectedClaim.claimStatus || "-"}</strong></div>
                                            <div><span>Policy Number</span><strong>{selectedClaim.policyNumber || "-"}</strong></div>
                                            <div><span>Client Type</span><strong>{selectedClaim.clientType || selectedClaim.policyId?.clientType || "-"}</strong></div>
                                            <div><span>Client Name</span><strong>{selectedClaim.policyId?.clientName || "-"}</strong></div>
                                            <div><span>Insurer</span><strong>{selectedClaim.policyId?.insurerName || "-"}</strong></div>
                                            <div><span>Policy Name</span><strong>{selectedClaim.policyId?.policyName || "-"}</strong></div>
                                            <div><span>Branch</span><strong>{selectedClaim.policyId?.branchName || "-"}</strong></div>
                                            <div><span>Policy Start</span><strong>{formatDate(selectedClaim.policyId?.policyStartDate)}</strong></div>
                                            <div><span>Policy End</span><strong>{formatDate(selectedClaim.policyId?.policyEndDate)}</strong></div>
                                            <div><span>Date of Loss</span><strong>{formatDate(selectedClaim.dateOfLoss)}</strong></div>
                                            <div><span>Date of Intimation</span><strong>{formatDate(selectedClaim.dateOfIntimation)}</strong></div>
                                            <div><span>Type of Loss</span><strong>{selectedClaim.typeOfLoss || "-"}</strong></div>
                                            <div><span>Estimated Loss</span><strong>{formatCurrency(selectedClaim.estimatedLoss)}</strong></div>
                                            <div className="claim-detail-wide"><span>Cause of Loss</span><strong>{selectedClaim.causeOfLoss || "-"}</strong></div>
                                        </div>

                                        {selectedClaim.claimDocPath && (
                                            <div className="claim-detail-document">
                                                <a
                                                    href={`${API_BASE_URL}/${selectedClaim.claimDocPath}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View Claim Document
                                                </a>
                                            </div>
                                        )}

                                        {selectedClaim.settlement?.finalStatus && (
                                            <>
                                                <hr />
                                                <h4>Settlement Details</h4>
                                                <div className="claim-detail-grid">
                                                    <div><span>Final Status</span><strong>{selectedClaim.settlement.finalStatus}</strong></div>
                                                    <div><span>Settlement Date</span><strong>{formatDate(selectedClaim.settlement.settlementDate)}</strong></div>
                                                    <div><span>Amount Paid</span><strong>{formatCurrency(selectedClaim.settlement.amountPaid)}</strong></div>
                                                    <div className="claim-detail-wide"><span>Rejection Reason</span><strong>{selectedClaim.settlement.rejectionReason || "-"}</strong></div>
                                                </div>
                                            </>
                                        )}

                                        <hr />
                                        <p><b>Claim Number:</b> {selectedClaim.claimNumber}</p>
                                        <p><b>Policy Number:</b> {selectedClaim.policyNumber}</p>
                                        <p><b>Type of Loss:</b> {selectedClaim.typeOfLoss}</p>
                                        <p>
                                            <label>Date of Loss</label>
                                            {isEditMode ? (
                                                <input
                                                    type="date"
                                                    value={editClaimData.dateOfLoss?.slice(0, 10)}
                                                    onChange={(e) =>
                                                        setEditClaimData({
                                                            ...editClaimData,
                                                            dateOfLoss: e.target.value
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <p>{new Date(selectedClaim.dateOfLoss).toLocaleDateString("en-IN")}</p>
                                            )}

                                        </p>
                                        <p>
                                            <label>Estimated Loss</label>
                                            {isEditMode ? (
                                                <input
                                                    type="number"
                                                    value={editClaimData.estimatedLoss}
                                                    onChange={(e) =>
                                                        setEditClaimData({
                                                            ...editClaimData,
                                                            estimatedLoss: e.target.value
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <p>₹ {selectedClaim.estimatedLoss.toLocaleString("en-IN")}</p>
                                            )}
                                        </p>
                                        <label>Type of Loss</label>
                                        {isEditMode ? (
                                            <select
                                                value={editClaimData.typeOfLoss}
                                                onChange={(e) =>
                                                    setEditClaimData({
                                                        ...editClaimData,
                                                        typeOfLoss: e.target.value
                                                    })
                                                }
                                            >
                                                <option value="Property">Property</option>
                                                <option value="Marine">Marine</option>
                                                <option value="Liability">Liability</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        ) : (
                                            <p>{selectedClaim.typeOfLoss}</p>
                                        )}

                                        <p><b>Status:</b> {selectedClaim.claimStatus}</p>

                                        {selectedClaim.surveyorAppointment && (
                                            <>
                                                <hr />
                                                <h4>Surveyor Details</h4>
                                                <p><b>Appointment Date:</b> {formatDate(selectedClaim.surveyorAppointment.appointmentDate)}</p>
                                                <p><b>Name:</b> {selectedClaim.surveyorAppointment.surveyorName}</p>
                                                <p><b>Phone:</b> {selectedClaim.surveyorAppointment.phoneNumber}</p>
                                                <p><b>Email:</b> {selectedClaim.surveyorAppointment.email}</p>
                                                <p><b>Address:</b> {selectedClaim.surveyorAppointment.address || "-"}</p>
                                                <p><b>IRDAI License:</b> {selectedClaim.surveyorAppointment.irdaLicenseNumber}</p>
                                                <p><b>Requirements:</b> {selectedClaim.surveyorAppointment.requirements || "-"}</p>
                                            </>
                                        )}

                                        <div className="modal-actions">
                                            {!isEditMode && canAddClaim ? (
                                                <button
                                                    className="secondary-btn"
                                                    onClick={() => setIsEditMode(true)}
                                                >
                                                    ✏️ Edit
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        className="submit-btn"
                                                        onClick={handleUpdateClaim}
                                                    >
                                                        💾 Save
                                                    </button>

                                                    <button
                                                        className="cancel-btn"
                                                        onClick={() => {
                                                            setIsEditMode(false);
                                                            setEditClaimData(selectedClaim);
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}

                        {statusSurveyorClaim && (
                            <div className="modal-overlay" onClick={() => setStatusSurveyorClaim(null)}>
                                <div className="modal-box surveyor-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <div>
                                            <span className="modal-eyebrow">Claim Assessment</span>
                                            <h3>Surveyor Details</h3>
                                        </div>
                                        <button onClick={() => setStatusSurveyorClaim(null)}>X</button>
                                    </div>

                                    <div className="surveyor-modal-body">
                                        <div className="surveyor-claim-strip">
                                            <div>
                                                <span>Claim Number</span>
                                                <strong>{statusSurveyorClaim.claimNumber}</strong>
                                            </div>
                                            <div>
                                                <span>Policy Number</span>
                                                <strong>{statusSurveyorClaim.policyNumber || "-"}</strong>
                                            </div>
                                            <div>
                                                <span>Estimated Loss</span>
                                                <strong>{formatCurrency(statusSurveyorClaim.estimatedLoss)}</strong>
                                            </div>
                                        </div>

                                        <div className="surveyor-section-title">
                                            <h4>Appointment</h4>
                                            <span>Required before moving this claim to Surveyor Assigned.</span>
                                        </div>

                                        <div className="surveyor-form-grid">
                                            <label>
                                                <span>Date of Appointment <b>*</b></span>
                                                <input
                                                    type="date"
                                                    value={statusSurveyorData.appointmentDate}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({ ...statusSurveyorData, appointmentDate: e.target.value })
                                                    }
                                                    required
                                                />
                                            </label>

                                            <label>
                                                <span>Surveyor Name <b>*</b></span>
                                                <input
                                                    type="text"
                                                    value={statusSurveyorData.surveyorName}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({ ...statusSurveyorData, surveyorName: e.target.value })
                                                    }
                                                    placeholder="Enter surveyor name"
                                                    required
                                                />
                                            </label>

                                            <label>
                                                <span>Phone Number <b>*</b></span>
                                                <input
                                                    type="text"
                                                    maxLength={10}
                                                    value={statusSurveyorData.phoneNumber}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({
                                                            ...statusSurveyorData,
                                                            phoneNumber: e.target.value.replace(/[^\d]/g, "")
                                                        })
                                                    }
                                                    placeholder="10 digit mobile number"
                                                    required
                                                />
                                            </label>

                                            <label>
                                                <span>Email <b>*</b></span>
                                                <input
                                                    type="email"
                                                    value={statusSurveyorData.email}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({ ...statusSurveyorData, email: e.target.value })
                                                    }
                                                    placeholder="surveyor@example.com"
                                                    required
                                                />
                                            </label>

                                            <label>
                                                <span>IRDAI License Number <b>*</b></span>
                                                <input
                                                    type="text"
                                                    value={statusSurveyorData.irdaLicenseNumber}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({
                                                            ...statusSurveyorData,
                                                            irdaLicenseNumber: e.target.value
                                                        })
                                                    }
                                                    placeholder="Enter license number"
                                                    required
                                                />
                                            </label>

                                            <label>
                                                <span>Address</span>
                                                <textarea
                                                    value={statusSurveyorData.address}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({ ...statusSurveyorData, address: e.target.value })
                                                    }
                                                    placeholder="Surveyor office or contact address"
                                                />
                                            </label>

                                            <label className="surveyor-wide-field">
                                                <span>Surveyor Requirements <b>*</b></span>
                                                <textarea
                                                    value={statusSurveyorData.requirements}
                                                    onChange={(e) =>
                                                        setStatusSurveyorData({ ...statusSurveyorData, requirements: e.target.value })
                                                    }
                                                    placeholder="Documents, inspection notes, or assessment requirements"
                                                    required
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    <div className="modal-actions surveyor-modal-actions">
                                        <button
                                            className="cancel-btn"
                                            onClick={() => {
                                                setStatusSurveyorClaim(null);
                                                setStatusSurveyorData(createEmptySurveyorDetails());
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button className="submit-btn" onClick={handleSurveyorStatusSave}>
                                            Save Surveyor
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showSettlementPopup && (
                            <div className="modal-overlay">
                                <div className="modal-box">
                                    <div className="modal-header">
                                        <h3>Claim Settlement</h3>
                                        <button onClick={() => setShowSettlementPopup(false)}>✕</button>
                                    </div>

                                    <label>Status</label>
                                    <select
                                        value={settlementData.finalStatus}
                                        onChange={(e) =>
                                            setSettlementData({
                                                ...settlementData,
                                                finalStatus: e.target.value
                                            })
                                        }
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>

                                    <label>Date of Settlement</label>
                                    <input
                                        type="date"
                                        value={settlementData.settlementDate}
                                        onChange={(e) =>
                                            setSettlementData({
                                                ...settlementData,
                                                settlementDate: e.target.value
                                            })
                                        }
                                    />

                                    {settlementData.finalStatus === "Approved" && (
                                        <>
                                            <label>Amount Paid</label>
                                            <input
                                                type="number"
                                                value={settlementData.amountPaid}
                                                onChange={(e) =>
                                                    setSettlementData({
                                                        ...settlementData,
                                                        amountPaid: e.target.value
                                                    })
                                                }
                                                required
                                            />
                                        </>
                                    )}

                                    {settlementData.finalStatus === "Rejected" && (
                                        <>
                                            <label>Reason for Rejection</label>
                                            <textarea
                                                value={settlementData.rejectionReason}
                                                onChange={(e) =>
                                                    setSettlementData({
                                                        ...settlementData,
                                                        rejectionReason: e.target.value
                                                    })
                                                }
                                                required
                                            />
                                        </>
                                    )}

                                    <div className="modal-actions">
                                        <button
                                            className="submit-btn"
                                            onClick={handleSettlementSave}
                                        >
                                            Save Settlement
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}



                        {/* <div className="policy-search-box" style={{ position: "relative" }}>
                    <label>Search Policy</label>
                    <input
                        type="text"
                        placeholder="Type Policy Number..."
                        value={policySearchInput}
                        onChange={(e) => {
                            setPolicySearchInput(e.target.value);
                            setSelectedIndex(-1);
                        }}
                        onFocus={() => setPolicyFocused(true)}
                        onBlur={() =>
                            setTimeout(() => {
                                setPolicySuggestions([]);
                                setPolicyFocused(false);
                            }, 200)
                        }
                        onKeyDown={(e) => {
                            if (e.key === "ArrowDown") {
                                setSelectedIndex(prev => Math.min(prev + 1, policySuggestions.length - 1));
                            } else if (e.key === "ArrowUp") {
                                setSelectedIndex(prev => Math.max(prev - 1, 0));
                            } else if (e.key === "Enter" && selectedIndex !== -1) {
                                handlePolicySelect(policySuggestions[selectedIndex]);
                            }
                        }}
                    />

                    {policyFocused && policySuggestions.length > 0 && (
                        <ul className="policy-suggestions">
                            {policySuggestions.map((item, idx) => (
                                <li
                                    key={idx}
                                    className={selectedIndex === idx ? "highlight" : ""}
                                    onMouseDown={() => handlePolicySelect(item)}
                                >
                                    <b>{item.policyNumber}</b><br />
                                    <small>{item.insurerName}</small>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {policyData && (
                    <>
                        <div className="policy-details-box">
                            <p><b>Client:</b> {policyData.clientName}</p>
                            <p><b>Insurer:</b> {policyData.insurerName}</p>
                            <p><b>Policy Name:</b> {policyData.policyName}</p>
                        </div>

                        <form className="claim-form" onSubmit={handleSubmit}>

                            <label>Claim Number</label>
                            <input
                                type="text"
                                name="claimNumber"
                                value={formData.claimNumber}
                                onChange={handleChange}
                                required
                            />

                            <label>Claim Type</label>
                            <select
                                name="claimType"
                                value={formData.claimType}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Type</option>
                                <option value="Cashless">Cashless</option>
                                <option value="Reimbursement">Reimbursement</option>
                                <option value="Accident Claim">Accident Claim</option>
                                <option value="Hospitalization">Hospitalization</option>
                                <option value="Theft">Theft</option>
                                <option value="Total Loss">Total Loss</option>
                                <option value="Partial Loss">Partial Loss</option>
                                <option value="Other">Other</option>
                            </select>

                            <label>Claim Amount</label>
                            <input
                                type="number"
                                name="claimAmount"
                                value={formData.claimAmount}
                                onChange={handleChange}
                                required
                            />

                            <label>Claim Date</label>
                            <input
                                type="date"
                                name="claimDate"
                                value={formData.claimDate}
                                onChange={handleChange}
                                required
                            />

                            <label>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                            ></textarea>

                            <label>Upload Claim Document</label>
                            <input
                                type="file"
                                onChange={(e) => setClaimDoc(e.target.files[0])}
                                required
                            />

                            <button type="submit" className="submit-btn">Submit Claim</button>
                        </form>
                    </>
                )} */}

                    </section>
                </div>
            </div>
        </div >
    );
};

export default Claims;
